import { Box, Chip } from "@analyticsMD/quil";
import { FC } from "react";
import moment from "moment-timezone";
import { css } from "@emotion/css";
import PatientInsightsNoteHeader from "../../types/PatientInsightsNoteHeader";
import { green, grey } from "@mui/material/colors";

const styles = {
  noteHeader: css`
    display: flex;
    align-items: center;
    gap: 0.5rem;
  `,
  noteHeaderTitle: css`
    padding-left: 0.35rem;
    font-size: 0.75rem;
    color: ${grey[600]};
    display: flex;
    align-items: center;
    gap: 0.5rem;
  `,
  avatar: css`
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: ${grey[600]};
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.65rem;
    font-weight: 500;
    flex-shrink: 0;
  `,
  timezone: (isToday: boolean) => css`
    color: ${isToday ? green[600] : grey[600]};
    font-size: 0.65rem;
  `,
};

const sxStyles = {
  noteHeaderDateTimeChip: (isToday: boolean) => ({
    backgroundColor: isToday ? green[50] : grey[100],
    color: isToday ? green[900] : "black",
    fontSize: "0.75rem",
  }),
};

const formatTimestamp = (timestamp: string) => {
  const noteMoment = moment(timestamp);
  const now = moment();
  const clientTimezone = moment.tz.guess();

  const getTimezoneDisplay = (momentObj: moment.Moment) => {
    const abbrev = momentObj.tz(clientTimezone).format("z");
    // If it's just numbers/offset, we'll use EST timezone
    const hasAbbreviation = !abbrev.match(/^[+-]\d+$/);

    if (hasAbbreviation) {
      return {
        time: momentObj.tz(clientTimezone).format("HH:mm"),
        timezone: clientTimezone,
        timezone_abbrev: abbrev,
      };
    } else {
      // Convert to EST/EDT and show proper timezone label
      const easternMoment = momentObj.tz("America/New_York");
      return {
        time: easternMoment.format("HH:mm"),
        timezone: "America/New_York",
        timezone_abbrev: easternMoment.format("z"),
      };
    }
  };

  // Check if it's today
  if (noteMoment.isSame(now, "day")) {
    const { time, timezone_abbrev } = getTimezoneDisplay(noteMoment);
    return { dateTime: `Today, ${time}`, timezone_abbrev };
  }

  // Check if it's yesterday
  if (noteMoment.isSame(now.clone().subtract(1, "day"), "day")) {
    const { time, timezone_abbrev } = getTimezoneDisplay(noteMoment);
    return { dateTime: `Yesterday, ${time}`, timezone_abbrev };
  }

  // For other dates, show the full date
  const { time, timezone, timezone_abbrev } = getTimezoneDisplay(noteMoment);
  return {
    dateTime: `${noteMoment.tz(timezone).format("M/D/YYYY")}, ${time}`,
    timezone_abbrev,
  };
};

const getInitials = (name: string): string => {
  return name
    .replace(/^(Dr|RN|MD)[.\s]*/i, "") // Remove title prefixes
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase())
    .slice(0, 2) // Take only first 2 initials
    .join("");
};

interface InsightItemNoteHeaderProps {
  noteHeader: PatientInsightsNoteHeader;
}

const InsightItemNoteHeader: FC<InsightItemNoteHeaderProps> = ({
  noteHeader,
}) => {
  const { dateTime, timezone_abbrev } = formatTimestamp(noteHeader.timestamp);
  const initials = getInitials(noteHeader.author);
  const isToday = dateTime.includes("Today");
  return (
    <Box className={styles.noteHeader}>
      <Chip
        label={
          <span>
            {dateTime}{" "}
            <span className={styles.timezone(isToday)}>{timezone_abbrev}</span>
          </span>
        }
        size="small"
        sx={sxStyles.noteHeaderDateTimeChip(isToday)}
      />
      <span className={styles.noteHeaderTitle}>
        <div className={styles.avatar}>{initials}</div>
        {noteHeader.author} {noteHeader.author_title}
      </span>
    </Box>
  );
};

export default InsightItemNoteHeader;
